import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'

import Button from '@/components/Button'

type DropDownButtonBase = {
  children: ReactNode
  disabled?: boolean
  id?: string
  title: string | ReactNode
  className?: string
  onSelected?: (value: string | number) => void
}

type DropDownButtonContextProps = {
  onSelectItem: (value: string | number) => void
}

const DropDownButtonContext = createContext<DropDownButtonContextProps | null>(null)

const DropDownButton = ({ children, disabled = false, id = '', title, onSelected = () => {} }: DropDownButtonBase) => {
  const [isOpen, setIsOpen] = useState(false)
  const buttonContainer = useRef<any>()
  const overlay = useRef<any>()
  const menu = useRef<any>()

  const handleClickOutside = (event: Event) => {
    const domNode = menu.current
    if (
      !domNode ||
      (!domNode.contains(event.target) && !buttonContainer.current.contains(event.target)) ||
      (overlay.current && overlay.current.contains(event.target))
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const onSelectItem = (value: number | string) => {
    setIsOpen(false)
    onSelected(value)
  }

  return (
    <DropDownButtonContext.Provider value={{ onSelectItem }}>
      <div className="dropdown floating-menu">
        <div ref={buttonContainer}>
          <Button color="primary" onClick={handleClick} disabled={disabled} id={id}>
            {title}
          </Button>
        </div>

        {isOpen && (
          <div ref={menu} className="dropdown-menu">
            {children}
          </div>
        )}
      </div>
    </DropDownButtonContext.Provider>
  )
}

const useDropDownButton = (): DropDownButtonContextProps => {
  const context = useContext(DropDownButtonContext)
  if (!context) throw new Error('useDropDownButton must be used within a DropDownButtonProvider')
  return context
}

DropDownButton.displayName = 'DropDownButton'

export { useDropDownButton }

export default DropDownButton
